import cx from 'classnames'
import React, { useEffect, useState, useContext } from 'react'
import WheelWell from './WheelWell'

import './App.scss'
import { API_GET_DOWNLOAD_URL, CHANNEL_PATH, jsonVersions } from './constants'
import { GraphContextProvider } from './GraphContext'
import { ControlMap, getControlsFromJsonFile, getControlsFromStringParams } from './GraphParamDefs'
import { defaultMintConfig, PrintContextProvider } from './PrintContext'
import { RawControlParamMap } from './types'
import realtimeService from './RealtimeService'
import UserContext, { UserContextType } from './UserContext'

type Props = {
  archivePath: string
  initialQueryString: string
  realtimeTimestamp: number
}
const GraphContainer = ({ archivePath, initialQueryString, realtimeTimestamp }: Props) => {
  const {
    userContext: { isFullscreen, isReadOnly, channelId, isReceiving },
    setVersion,
  } = useContext(UserContext) as UserContextType
  const [requestedFrameNumber, setRequestedFrameNumber] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const [initialMintConfig, setInitialMintConfig] = useState(defaultMintConfig)
  const getInitialControlsFromQuery = () => {
    const queryParamArray = initialQueryString.split('&')
    const queryParamMap: RawControlParamMap = queryParamArray.reduce((acc, paramString) => {
      if (!paramString) {
        return acc
      }
      const [key, value] = paramString.split('=')
      return { ...acc, [key]: value }
    }, {})
    return getControlsFromStringParams(queryParamMap)
  }
  const [initialConfig, setInitialConfig] = useState<ControlMap | null>(null)

  const getInitialControlsFromJson = (json: string, requestedFrame?: number) => {
    const fileObject = JSON.parse(json)
    const { version, graph, position, rotation = {}, colors, mintHistory } = fileObject
    if (!jsonVersions.includes(version)) {
      console.log(`unsupported json version [${version}]!`)
      return
    }
    const newMintHistory = {
      ...defaultMintConfig,
      ...mintHistory,
    }
    if (version === '1.0.0') {
      // newly uploaded 1.0.0 json will have rotZ defined, files created pre 1.1.0 won't
      if (rotation.rotZ === undefined) {
        rotation.rotZ = position.rot
      }
    }
    const controlParams = { ...graph, ...position, ...rotation, ...colors, version: { version } }
    setInitialConfig(getControlsFromJsonFile(controlParams))
    setInitialMintConfig(newMintHistory)
    setVersion(version)
    setIsReady(true)
    const frameNumber = requestedFrame || mintHistory.frameNumber
    if (frameNumber >= 1) {
      setRequestedFrameNumber(frameNumber)
    }
  }
  const getInitialControlsFromFile = (selectedFile: File) => {
    setIsReady(false)
    const reader = new FileReader()
    reader.onload = (event) => {
      const fileStr = event.target ? event.target.result : ''
      console.log('loaded:', fileStr)
      if (fileStr) {
        getInitialControlsFromJson(fileStr.toString())
        window.history.replaceState({}, '', '/')
      }
    }
    reader.readAsText(selectedFile)
  }

  const { frameNumber: initialFrameNumber = 1 } = initialMintConfig
  const firstDisplayFrameNumber =
    requestedFrameNumber > 0 ? requestedFrameNumber : initialFrameNumber
  const className = cx('graphContainer', {
    fullScreen: isFullscreen,
    ready: isReady,
    message: !isReady && isReadOnly,
  })

  useEffect(() => {
    if (!isReceiving || !realtimeTimestamp) {
      return
    }
    setIsReady(false)
    realtimeService.endpointOnce(`${CHANNEL_PATH}/${channelId}/graph`, '').then((graphJson) => {
      getInitialControlsFromJson(graphJson)
    })
  }, [isReceiving, realtimeTimestamp])

  useEffect(() => {
    setIsReady(false)
    const archiveParts = archivePath.split('/')
    if (!archivePath || archiveParts.length < 2) {
      if (!isReadOnly) {
        setInitialConfig(getInitialControlsFromQuery())
        const newUrl = new URL(window.origin)
        window.history.replaceState({}, '', newUrl) // hide our big url
      }
      setIsReady(true)
      return
    }
    const [type, name, frame = '1'] = archiveParts
    const getInitialControlsFromPath = async () => {
      const filePath = `${type}/${name}`
      const apiUrl = `${API_GET_DOWNLOAD_URL}/${encodeURIComponent(filePath)}.json`
      const urlResponse = await fetch(apiUrl)
      const { signedUrl } = await urlResponse.json()
      const response = await fetch(signedUrl)
      if (response.status !== 200) {
        throw new Error(`Couldn't load ${apiUrl}`)
      }
      const fileText = await response.text()
      getInitialControlsFromJson(fileText, parseInt(frame))
    }
    getInitialControlsFromPath()
      .then(() => {
        setIsReady(true)
      })
      .catch((error) => {
        console.log(error)
        alert(error)
      })
  }, [archivePath])
  // TODO: solve dependency warning
  // however, wrapping getInititalControlsFromJson in useCallback and adding as a dependency causes infinite rerenders)

  return (
    <div className={className}>
      {isReady && initialConfig && (
        <PrintContextProvider initialMintConfig={initialMintConfig}>
          <GraphContextProvider
            initialControls={initialConfig}
            firstFrameOffset={initialFrameNumber}
            firstDisplayFrameNumber={firstDisplayFrameNumber}
          >
            <WheelWell onLoadFile={getInitialControlsFromFile} />
          </GraphContextProvider>
        </PrintContextProvider>
      )}
      {isReadOnly && channelId && !isReceiving && <div>Awaiting active channel...</div>}
    </div>
  )
}

export default GraphContainer
